import * as Sentry from '@sentry/nextjs'
import type { ScopeContext } from '@sentry/core'
import { readStoredAuthSessionPresence } from '../auth/sessionStorage'

type SentryUserInput = {
  email?: string | null
  name?: string | null
  sub?: string | null
}

type ResolvedSentryIdentity = {
  authMode: 'anonymous' | 'app-session' | 'auth0'
  sessionPresent: boolean
  user?: {
    email?: string
    id: string
    username?: string
  }
}

const BROWSER_USER_PREFIX = 'browser'
const BROWSER_ID_STORAGE_KEY = 'sentryBrowserId'
const LEGACY_BROWSER_ID_STORAGE_KEY = 'userId'

const createBrowserId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`
}

const readOrCreateBrowserId = () => {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const storedBrowserId =
      window.localStorage.getItem(BROWSER_ID_STORAGE_KEY) ||
      window.localStorage.getItem(LEGACY_BROWSER_ID_STORAGE_KEY)

    if (storedBrowserId) {
      window.localStorage.setItem(BROWSER_ID_STORAGE_KEY, storedBrowserId)
      return storedBrowserId
    }

    const browserId = createBrowserId()
    window.localStorage.setItem(BROWSER_ID_STORAGE_KEY, browserId)
    return browserId
  } catch {
    return null
  }
}

const buildBrowserUserId = (browserId: string) =>
  `${BROWSER_USER_PREFIX}:${browserId}`

export const resolveSentryIdentity = (
  user: SentryUserInput | null | undefined
): ResolvedSentryIdentity => {
  const browserId = readOrCreateBrowserId()
  const browserUserId = browserId ? buildBrowserUserId(browserId) : undefined
  const sessionPresent = readStoredAuthSessionPresence()
  const auth0Id = user?.sub || user?.email
  if (auth0Id) {
    return {
      authMode: 'auth0',
      sessionPresent,
      user: {
        id: auth0Id,
        email: user?.email || undefined,
        username: user?.name || undefined,
      },
    }
  }

  if (sessionPresent) {
    return {
      authMode: 'app-session',
      sessionPresent: true,
      user: browserUserId
        ? {
            id: browserUserId,
          }
        : {
            id: 'app-session',
          },
    }
  }

  if (browserUserId) {
    return {
      authMode: 'anonymous',
      sessionPresent: false,
      user: {
        id: browserUserId,
      },
    }
  }

  return {
    authMode: 'anonymous',
    sessionPresent: false,
  }
}

export const buildInitialSentryScope = (
  user?: SentryUserInput | null
): Partial<ScopeContext> => {
  const identity = resolveSentryIdentity(user)

  return {
    tags: {
      'auth.mode': identity.authMode,
      'auth.session_present': identity.sessionPresent ? 'true' : 'false',
    },
    user: identity.user,
  }
}

export const syncSentryUserContext = (user?: SentryUserInput | null) => {
  const identity = resolveSentryIdentity(user)

  Sentry.setTag('auth.mode', identity.authMode)
  Sentry.setTag(
    'auth.session_present',
    identity.sessionPresent ? 'true' : 'false'
  )
  Sentry.setUser(identity.user ?? null)
}
